import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../../components/Layout';
import { Ic } from '../../design/icons';
import { Avatar, Chip, SectionCard } from '../../design/primitives';

const API = `${window.location.origin}/api`;

const AUDIENCE_OPTIONS = [
  { value: 'all_users', label: 'Tüm Kullanıcılar', hint: 'Sistemdeki aktif tüm kullanıcılar' },
  { value: 'active_subscribers', label: 'Aktif Aboneler', hint: 'Aktif aboneliği olan workspace sahipleri' },
  { value: 'plan', label: 'Plan Bazlı', hint: 'Belirli bir plana sahip workspace sahipleri' },
  { value: 'workspace', label: 'Workspace', hint: 'Seçilen workspace üyeleri ve sahibi' },
  { value: 'manual', label: 'Manuel Liste', hint: 'Tek tek seçilen kullanıcılar' },
];

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'scale', label: 'Scale' },
  { value: 'unlimited', label: 'Sınırsız' },
];

export default function EmailCampaigns() {
  usePageHeader([{ label: 'Süper Admin' }, { label: 'E-posta Kampanyaları' }], null, []);
  const navigate = useNavigate();

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('<p>Merhaba,</p>\n<p>Burada mesajınız.</p>');
  const [audienceType, setAudienceType] = useState('all_users');
  const [planFilter, setPlanFilter] = useState('starter');
  const [workspaceIds, setWorkspaceIds] = useState([]);
  const [userIds, setUserIds] = useState([]);

  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Lazy load workspaces on demand
  useEffect(() => {
    if (audienceType !== 'workspace' || workspaceOptions.length > 0) return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/super-admin/workspaces?limit=200`);
        if (active) setWorkspaceOptions(res.data.workspaces || []);
      } catch (err) {
        if (active) toast.error("Workspace'ler yüklenemedi");
      }
    })();
    return () => { active = false; };
  }, [audienceType, workspaceOptions.length]);

  // Lazy load users on demand
  useEffect(() => {
    if (audienceType !== 'manual') return;
    let active = true;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: '50' });
        if (userSearch.trim()) params.append('q', userSearch.trim());
        const res = await axios.get(`${API}/super-admin/users?${params.toString()}`);
        if (active) setUserOptions(res.data.users || []);
      } catch (err) {
        // ignore
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [audienceType, userSearch]);

  const audienceFilter = useMemo(() => {
    if (audienceType === 'plan') return { plan: planFilter };
    if (audienceType === 'workspace') return { workspaceIds };
    if (audienceType === 'manual') return { userIds };
    return {};
  }, [audienceType, planFilter, workspaceIds, userIds]);

  const audienceValid = useMemo(() => {
    if (audienceType === 'workspace') return workspaceIds.length > 0;
    if (audienceType === 'manual') return userIds.length > 0;
    return true;
  }, [audienceType, workspaceIds.length, userIds.length]);

  // Reset preview when audience changes
  useEffect(() => { setPreview(null); }, [audienceType, planFilter, workspaceIds, userIds]);

  const runPreview = useCallback(async () => {
    if (!audienceValid) {
      toast.error('Lütfen hedef kitle seçimini tamamlayın');
      return;
    }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/super-admin/campaigns/preview`, {
        audienceType,
        audienceFilter,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Önizleme alınamadı');
    } finally {
      setPreviewing(false);
    }
  }, [audienceType, audienceFilter, audienceValid]);

  const send = useCallback(async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/super-admin/campaigns`, {
        subject: subject.trim(),
        bodyHtml,
        audienceType,
        audienceFilter,
      });
      toast.success(`Kampanya kuyruğa alındı (${res.data.recipientCount} alıcı)`);
      setConfirmOpen(false);
      navigate('/super-admin/campaigns/history');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gönderim başarısız');
    } finally {
      setSending(false);
    }
  }, [subject, bodyHtml, audienceType, audienceFilter, navigate]);

  const subjectValid = subject.trim().length > 0 && subject.trim().length <= 200;
  const bodyValid = bodyHtml.trim().length > 0;
  const canPreview = subjectValid && bodyValid && audienceValid;
  const canSend = canPreview && preview && preview.recipientCount > 0;

  const toggleWorkspace = (id) => {
    setWorkspaceIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleUser = (id) => {
    setUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <div className="ox-page space-y-4" style={{ padding: 24 }}>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)' }}>
        <SectionCard title="Kampanya bestecisi">
          <div className="space-y-3">
            <div className="ox-field">
              <label>Konu</label>
              <input
                className="ox-input"
                placeholder="E-posta konusu (max 200 karakter)"
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <div className="text-[11px] ox-text-muted mt-1">{subject.length} / 200</div>
            </div>

            <div className="ox-field">
              <label>HTML İçerik</label>
              <textarea
                className="ox-input"
                rows={12}
                style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 }}
                placeholder="<p>Merhaba,</p>"
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
              />
              <div className="text-[11px] ox-text-muted mt-1">
                Saf HTML kullanılır. OnxFlow başlık ve alt bilgi otomatik eklenir.
              </div>
            </div>

            <div className="ox-field">
              <label>Hedef Kitle</label>
              <div className="space-y-1.5">
                {AUDIENCE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-start gap-2 p-2 rounded cursor-pointer"
                    style={{
                      background: audienceType === opt.value ? 'var(--surface-muted)' : 'transparent',
                      border: '1px solid',
                      borderColor: audienceType === opt.value ? 'var(--accent)' : 'var(--border)',
                    }}
                  >
                    <input
                      type="radio"
                      checked={audienceType === opt.value}
                      onChange={() => setAudienceType(opt.value)}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div className="text-[13px] font-medium">{opt.label}</div>
                      <div className="text-[11.5px] ox-text-muted">{opt.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {audienceType === 'plan' && (
              <div className="ox-field">
                <label>Plan</label>
                <select className="ox-select" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                  {PLAN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            )}

            {audienceType === 'workspace' && (
              <div className="ox-field">
                <label>Workspace'ler ({workspaceIds.length} seçili)</label>
                <div
                  className="ox-surface rounded-md p-2 space-y-1"
                  style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)' }}
                >
                  {workspaceOptions.length === 0 ? (
                    <div className="text-[12px] ox-text-muted p-2">Yükleniyor...</div>
                  ) : workspaceOptions.map((w) => (
                    <label key={w.id} className="flex items-center gap-2 p-1.5 rounded cursor-pointer"
                      style={{ background: workspaceIds.includes(w.id) ? 'var(--surface-muted)' : 'transparent' }}>
                      <input type="checkbox"
                        checked={workspaceIds.includes(w.id)}
                        onChange={() => toggleWorkspace(w.id)} />
                      <span className="text-[12.5px] truncate flex-1">{w.name}</span>
                      <span className="text-[11px] ox-text-muted">{w.plan} · {w.memberCount || 0} üye</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {audienceType === 'manual' && (
              <div className="ox-field">
                <label>Kullanıcılar ({userIds.length} seçili)</label>
                <input
                  className="ox-input mb-2"
                  placeholder="İsim veya e-posta ara..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <div
                  className="ox-surface rounded-md p-2 space-y-1"
                  style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)' }}
                >
                  {userOptions.length === 0 ? (
                    <div className="text-[12px] ox-text-muted p-2">Sonuç yok</div>
                  ) : userOptions.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 p-1.5 rounded cursor-pointer"
                      style={{ background: userIds.includes(u.id) ? 'var(--surface-muted)' : 'transparent' }}>
                      <input type="checkbox"
                        checked={userIds.includes(u.id)}
                        onChange={() => toggleUser(u.id)} />
                      <Avatar u={u} size={20} />
                      <span className="text-[12.5px] truncate flex-1">{u.name || '—'}</span>
                      <span className="text-[11px] ox-text-muted truncate" style={{ maxWidth: 180 }}>{u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCard>

        <div className="space-y-4">
          <SectionCard title="Önizleme">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button
                  className="ox-btn ox-btn-ghost"
                  onClick={runPreview}
                  disabled={!canPreview || previewing}
                >
                  {Ic.search} {previewing ? 'Hesaplanıyor...' : 'Hedef kitleyi hesapla'}
                </button>
              </div>
              {preview && (
                <div className="ox-surface rounded-md p-3" style={{ background: 'var(--surface-muted)' }}>
                  <div className="text-[20px] font-semibold ox-tnum">
                    {preview.recipientCount}
                  </div>
                  <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                    Tahmini alıcı
                  </div>
                  {preview.sample && preview.sample.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {preview.sample.map((s) => (
                        <Chip key={s.id || s.email} color="var(--text-muted)" bg="var(--surface)">
                          {s.email}
                        </Chip>
                      ))}
                      {preview.recipientCount > preview.sample.length && (
                        <span className="text-[11px] ox-text-muted">
                          +{preview.recipientCount - preview.sample.length} daha
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard title="HTML önizleme">
            <iframe
              title="campaign-preview"
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:'Segoe UI',sans-serif;margin:0;padding:14px;color:#222}</style></head><body>${bodyHtml || '<p style="color:#999">Önizleme için içerik girin</p>'}</body></html>`}
              style={{
                width: '100%',
                height: 280,
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: '#fff',
              }}
            />
          </SectionCard>

          <SectionCard title="Gönderim">
            <div className="text-[12px] ox-text-muted mb-2">
              Gmail SMTP günlük ~500 mail sınırına dikkat edin. Büyük listeleri parçalara bölmek isteyebilirsiniz.
            </div>
            <button
              className="ox-btn"
              style={{ width: '100%' }}
              disabled={!canSend || sending}
              onClick={() => setConfirmOpen(true)}
            >
              {sending ? 'Gönderiliyor...' : preview ? `${preview.recipientCount} alıcıya gönder` : 'Önce önizleme alın'}
            </button>
          </SectionCard>
        </div>
      </div>

      {confirmOpen && (
        <div className="ox-modal-backdrop" onClick={() => !sending && setConfirmOpen(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Kampanyayı gönder</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setConfirmOpen(false)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body space-y-2">
              <p className="text-[13px]">
                Bu kampanya <strong>{preview?.recipientCount}</strong> alıcıya gönderilecek.
              </p>
              <div className="ox-surface rounded-md p-2.5" style={{ background: 'var(--surface-muted)' }}>
                <div className="text-[11px] ox-text-muted uppercase tracking-wider">Konu</div>
                <div className="text-[13px] font-medium">{subject}</div>
              </div>
              <p className="text-[12px] ox-text-muted">
                Gönderim arka planda işlenir. Geçmiş ekranından ilerlemeyi takip edebilirsiniz.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn ox-btn-ghost" disabled={sending} onClick={() => setConfirmOpen(false)}>İptal</button>
              <button className="ox-btn" disabled={sending} onClick={send}>
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
